import { ConsultantEventLogDataFragment, SMSDeliveryStatus } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import {
  CallIcon,
  ChatBubbleIcon,
  CircleWithIcon,
  Color,
  DraftSettingsIcon,
  FavoriteIcon,
  MailIcon,
  MobilePhoneIcon,
  NewsIcon,
  NotificationIcon,
  PersonCheckIcon,
  PhoneDisabledIcon,
  SelectMenu,
  UpdatesIcon,
  PersonUpdateIcon
} from "@bonliva-crm/ui";
import { Box, Chip, Typography } from "@material-ui/core";
import { transparentize } from "polished";
import { EventEntry, EventEntryProps } from "../components/event";
import { Description } from "../components/description";
import { FilterType, MainFilterType, OtherFilterType } from "./filter-type";

type ComponentProps = Partial<EventEntryProps>;

export const createFilteredEventComponents = ({
  filters,
  events,
  onUpdated
}: {
  filters: FilterType[];
  events: Record<FilterType, ConsultantEventLogDataFragment[]> | null;
  onUpdated: () => void;
}) => {
  if (!events) return [];

  const componentProps: ComponentProps = {
    onUpdated
  };

  const filteredComponents: {
    component: JSX.Element;
    createdAt: Date;
    sticky: boolean;
  }[] = [];

  if (filters.length === 0) {
    filteredComponents.push(
      ...createNoteComponents(events[MainFilterType.Comment], componentProps)
    );
    filteredComponents.push(
      ...createReminderComponents(events[OtherFilterType.Reminder], componentProps)
    );
    filteredComponents.push(
      ...createCallComponents(events[MainFilterType.Call], componentProps)
    );
    filteredComponents.push(
      ...createSmsComponents(events[MainFilterType.SMS], componentProps)
    );
    filteredComponents.push(
      ...createNoAnswerComponents(events[MainFilterType.Noanswer], componentProps)
    );
    filteredComponents.push(
      ...createInterestComponents(events[MainFilterType.Interest], componentProps)
    );
    filteredComponents.push(
      ...createEmailComponents(events[MainFilterType.Email], componentProps)
    );
    filteredComponents.push(
      ...createGDPRComponents(events[OtherFilterType.GDPR], componentProps)
    );
    filteredComponents.push(
      ...createQualityUpdateComponents(
        events[OtherFilterType.QualityUpdate],
        componentProps
      )
    );
    filteredComponents.push(
      ...createRegisteredComponents(events[OtherFilterType.Registered], componentProps)
    );
    filteredComponents.push(
      ...createSystemLogComponents(events[OtherFilterType.SystemLog], componentProps)
    );
    filteredComponents.push(
      ...createConsultantAppComponents(
        events[OtherFilterType.ConsultantApp],
        componentProps
      )
    );
    filteredComponents.push(
      ...createWelcomeEmailComponents(
        events[OtherFilterType.WelcomeEmail],
        componentProps
      )
    );
    filteredComponents.push(
      ...createUserSettingsComponents(
        events[OtherFilterType.UserSettings],
        componentProps
      )
    );
  } else {
    filters.forEach((filter) => {
      switch (filter) {
        case MainFilterType.Call:
          filteredComponents.push(
            ...createCallComponents(events[MainFilterType.Call], componentProps)
          );
          break;
        case MainFilterType.Noanswer:
          filteredComponents.push(
            ...createNoAnswerComponents(events[MainFilterType.Noanswer], componentProps)
          );
          break;
        case MainFilterType.Email:
          filteredComponents.push(
            ...createEmailComponents(events[MainFilterType.Email], componentProps)
          );
          break;
        case MainFilterType.SMS:
          filteredComponents.push(
            ...createSmsComponents(events[MainFilterType.SMS], componentProps)
          );
          break;
        case MainFilterType.Comment:
          filteredComponents.push(
            ...createNoteComponents(events[MainFilterType.Comment], componentProps)
          );
          break;
        case MainFilterType.Interest:
          filteredComponents.push(
            ...createInterestComponents(events[MainFilterType.Interest], componentProps)
          );
          break;
        case OtherFilterType.GDPR:
          filteredComponents.push(
            ...createGDPRComponents(events[OtherFilterType.GDPR], componentProps)
          );
          break;
        case OtherFilterType.QualityUpdate:
          filteredComponents.push(
            ...createQualityUpdateComponents(
              events[OtherFilterType.QualityUpdate],
              componentProps
            )
          );
          break;
        case OtherFilterType.Reminder:
          filteredComponents.push(
            ...createReminderComponents(events[OtherFilterType.Reminder], componentProps)
          );
          break;
        case OtherFilterType.Registered:
          filteredComponents.push(
            ...createRegisteredComponents(
              events[OtherFilterType.Registered],
              componentProps
            )
          );
          break;
        case OtherFilterType.SystemLog:
          filteredComponents.push(
            ...createSystemLogComponents(
              events[OtherFilterType.SystemLog],
              componentProps
            )
          );
          break;
        case OtherFilterType.ConsultantApp:
          filteredComponents.push(
            ...createConsultantAppComponents(
              events[OtherFilterType.ConsultantApp],
              componentProps
            )
          );
          break;
        case OtherFilterType.WelcomeEmail:
          filteredComponents.push(
            ...createWelcomeEmailComponents(
              events[OtherFilterType.WelcomeEmail],
              componentProps
            )
          );
          break;
        case OtherFilterType.UserSettings:
          filteredComponents.push(
            ...createUserSettingsComponents(
              events[OtherFilterType.UserSettings],
              componentProps
            )
          );
          break;
        default:
          throw new Error(`Unknown filter type ${filter}`);
      }
    });
  }
  return filteredComponents
    .sort((a, b) => {
      if (a.sticky && !b.sticky) return -1;
      if (!a.sticky && b.sticky) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    })
    .map((e) => e.component);
};

const createNoteComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((note) => ({
    component: (
      <EventEntry
        key={note._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Brown}
            icon={<NewsIcon htmlColor={Color.White} style={{ paddingBottom: "1px" }} />}
          />
        }
        event={note}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: note.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(note.CreatedOn),
    sticky: note.Sticky
  }));
};

const createReminderComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((reminder) => ({
    component: (
      <EventEntry
        key={reminder._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Information}
            icon={
              <NotificationIcon htmlColor={Color.White} style={{ paddingRight: "1px" }} />
            }
          />
        }
        event={reminder}
        content={() => (
          <Box style={{ display: "flex", flexDirection: "column", rowGap: "0.5rem" }}>
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              style={{
                width: "fit-content",
                backgroundColor: transparentize(0.75, Color.DoveBlue),
                border: `0.5px solid ${Color.DoveBlue}`,
                color: Color.Headings
              }}
              label={
                <Box
                  style={{ display: "flex", flexDirection: "row", columnGap: "0.25rem" }}
                >
                  <Typography variant="body1">{locale.Assigned}: </Typography>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {reminder.TodoItem?.AssignedTo?.Name ??
                      reminder.TodoItem?.AssignedTeam}
                  </Typography>
                </Box>
              }
            ></Chip>
            <Description>{reminder.TodoItem?.Description}</Description>
          </Box>
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(reminder.CreatedOn),
    sticky: reminder.Sticky
  }));
};

const createCallComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((call) => ({
    component: (
      <EventEntry
        key={call._id}
        icon={
          <CircleWithIcon
            circleColor={Color.DoveBlue}
            icon={<CallIcon htmlColor={Color.White} style={{ marginLeft: "1px" }} />}
          />
        }
        event={call}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: call.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(call.CreatedOn),
    sticky: call.Sticky
  }));
};

const createSmsComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((sms) => ({
    component: (
      <EventEntry
        key={sms._id}
        icon={
          <CircleWithIcon
            circleColor={Color.LightBlue}
            icon={<ChatBubbleIcon htmlColor={Color.Body} />}
          />
        }
        event={sms}
        content={() => {
          const color =
            sms.SMSMessage?.Status === SMSDeliveryStatus.Delivered
              ? Color.DarkSeaGreen
              : sms.SMSMessage?.Status === SMSDeliveryStatus.Pending
              ? Color.DoveBlue
              : Color.ErrorLight;
          return (
            <Box style={{ display: "flex", flexDirection: "column", rowGap: "0.5rem" }}>
              <Chip
                size="small"
                variant="outlined"
                color="secondary"
                style={{
                  width: "fit-content",
                  backgroundColor: transparentize(0.75, color),
                  border: `0.5px solid ${color}`,
                  color: Color.Headings
                }}
                label={
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      columnGap: "0.25rem"
                    }}
                  >
                    <Typography variant="body1">{locale.Status}: </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {sms.SMSMessage?.Status
                        ? locale.smsDeliveryStatusType[sms.SMSMessage?.Status]
                        : locale.smsDeliveryStatusType.Unknown}
                    </Typography>
                  </Box>
                }
              ></Chip>
              <Description
                dangerouslySetInnerHTML={{ __html: sms.DescriptionHtml }}
                reduceVerticalMargins
              />
            </Box>
          );
        }}
        {...componentProps}
      />
    ),
    createdAt: new Date(sms.CreatedOn),
    sticky: sms.Sticky
  }));
};

const createNoAnswerComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((noAnswer) => ({
    component: (
      <EventEntry
        key={noAnswer._id}
        icon={
          <CircleWithIcon
            circleColor={Color.JeansBlue}
            icon={
              <PhoneDisabledIcon htmlColor={Color.White} style={{ marginLeft: "1px" }} />
            }
          />
        }
        event={noAnswer}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: noAnswer.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(noAnswer.CreatedOn),
    sticky: noAnswer.Sticky
  }));
};

const createInterestComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((interest) => ({
    component: (
      <EventEntry
        key={interest._id}
        icon={
          <CircleWithIcon
            circleColor={Color.PrimaryLight}
            icon={<FavoriteIcon htmlColor={Color.Body} />}
          />
        }
        event={interest}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: interest.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(interest.CreatedOn),
    sticky: interest.Sticky
  }));
};

const createEmailComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((email) => ({
    component: (
      <EventEntry
        key={email._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Powder}
            icon={<MailIcon htmlColor={Color.Body} />}
          />
        }
        event={email}
        content={() => (
          <Box style={{ display: "flex", flexDirection: "column", rowGap: "0.5rem" }}>
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              style={{
                width: "fit-content",
                backgroundColor: transparentize(0.75, Color.DoveBlue),
                border: `0.5px solid ${Color.DoveBlue}`,
                color: Color.Headings
              }}
              label={
                <Box
                  style={{ display: "flex", flexDirection: "row", columnGap: "0.25rem" }}
                >
                  <Typography variant="body1">{locale.Recipients}: </Typography>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {email.EmailRecipients?.map((recipient) => recipient.Name).join(", ")}
                  </Typography>
                </Box>
              }
            ></Chip>
            <SelectMenu
              value={
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.25rem"
                  }}
                >
                  <Typography variant="subtitle1" style={{ color: Color.Headings }}>
                    {locale.Subject}:{" "}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 500, color: Color.Headings }}
                  >
                    {email.EmailSubject}
                  </Typography>
                </Box>
              }
              content={<div dangerouslySetInnerHTML={{ __html: email.EmailBody }} />}
            />
          </Box>
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(email.CreatedOn),
    sticky: email.Sticky
  }));
};

const createGDPRComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((gdpr) => ({
    component: (
      <EventEntry
        key={gdpr._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Information}
            icon={
              <Typography
                variant="body1"
                style={{
                  fontSize: "0.625rem",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  color: Color.White,
                  marginLeft: "-5px",
                  marginTop: "2px"
                }}
              >
                {locale.GDPR}
              </Typography>
            }
          />
        }
        event={gdpr}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: gdpr.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(gdpr.CreatedOn),
    sticky: gdpr.Sticky
  }));
};

const createQualityUpdateComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((qualityUpdate) => ({
    component: (
      <EventEntry
        key={qualityUpdate._id}
        icon={<CircleWithIcon circleColor={Color.Information} icon={<UpdatesIcon />} />}
        event={qualityUpdate}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: qualityUpdate.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(qualityUpdate.CreatedOn),
    sticky: qualityUpdate.Sticky
  }));
};

const createRegisteredComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((registered) => ({
    component: (
      <EventEntry
        key={registered._id}
        icon={
          <CircleWithIcon circleColor={Color.Information} icon={<PersonCheckIcon />} />
        }
        event={registered}
        content={() => (
          <Typography variant="body1">
            {registered.Description || locale.Registration}
          </Typography>
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(registered.CreatedOn),
    sticky: registered.Sticky
  }));
};

const createSystemLogComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((systemLog) => ({
    component: (
      <EventEntry
        key={systemLog._id}
        icon={
          <CircleWithIcon circleColor={Color.Information} icon={<DraftSettingsIcon />} />
        }
        event={systemLog}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: systemLog.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(systemLog.CreatedOn),
    sticky: systemLog.Sticky
  }));
};

const createConsultantAppComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((consultantApp) => ({
    component: (
      <EventEntry
        key={consultantApp._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Information}
            icon={<MobilePhoneIcon style={{ paddingRight: "1px" }} />}
          />
        }
        event={consultantApp}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: consultantApp.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(consultantApp.CreatedOn),
    sticky: consultantApp.Sticky
  }));
};

const createWelcomeEmailComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((welcomeEmail) => ({
    component: (
      <EventEntry
        key={welcomeEmail._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Information}
            icon={<MailIcon htmlColor={Color.White} />}
          />
        }
        event={welcomeEmail}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: welcomeEmail.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(welcomeEmail.CreatedOn),
    sticky: welcomeEmail.Sticky
  }));
};

const createUserSettingsComponents = (
  events: ConsultantEventLogDataFragment[],
  componentProps: ComponentProps
) => {
  return events.map((userSettings) => ({
    component: (
      <EventEntry
        key={userSettings._id}
        icon={
          <CircleWithIcon circleColor={Color.Information} icon={<PersonUpdateIcon />} />
        }
        event={userSettings}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: userSettings.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(userSettings.CreatedOn),
    sticky: userSettings.Sticky
  }));
};
